import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Megaphone, HeartHandshake, Users, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { EmailCapture } from "@/components/EmailCapture";
import { StayConnected } from "@/components/StayConnected";
import { AnthemVideo } from "@/components/AnthemVideo";

const IMG = {
  landscape:
    "https://images.pexels.com/photos/34610640/pexels-photo-34610640.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  sofia:
    "https://customer-assets-4nw71qhi.emergentagent.net/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/5c1325956ab0455cb074e056a198923b_image.bin",
  gloria:
    "https://customer-assets-4nw71qhi.emergentagent.net/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/8adc5beadfe7417cb67885889c566dac_image.bin",
  tom:
    "https://customer-assets-4nw71qhi.emergentagent.net/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/4355cad9e652485cb454c37598766367_image.bin",
  phoebe:
    "https://customer-assets-4nw71qhi.emergentagent.net/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/f7bfd2cb3a88433a8f7bace5bd060fd2_image.bin",
  rex:
    "https://customer-assets-4nw71qhi.emergentagent.net/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/1c0ff1ee790744deb5bfeed81c48ec85_image.bin",
  ricco:
    "https://customer-assets-4nw71qhi.emergentagent.net/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/81aebb0e21814ce088c2d60785817067_image.bin",
  john:
    "https://static.prod-images.emergentagent.com/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/images/3e07929d5ad5888575da788eac7584aa55d7304a1ac8ceec482938b13564091e.jpeg",
};

const HeroLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const PILLARS = [
  {
    icon: Megaphone,
    title: "Causes find visibility",
    body: "Every town has a cause worth fighting for. We help them tell their story — loudly, warmly, and to the people who care.",
    testid: "pillar-causes",
  },
  {
    icon: HeartHandshake,
    title: "Givers find purpose",
    body: "Giving should feel like coming home. We connect generous hearts with the causes that turn dollars into difference.",
    testid: "pillar-givers",
  },
  {
    icon: Users,
    title: "Communities find support",
    body: "When a cause rises, a whole community rises with it. That's the circle we're building — one town at a time.",
    testid: "pillar-communities",
  },
];

// Last names confirmed by user: Gloria Hartwell, Sofia Reyes.
const PEOPLE = [
  {
    name: "Rev. Thomas Carter",
    role: "Faith & Community Council",
    img: "/images/rev-thomas-carter.jpg",
    alt: "Rev. Thomas Carter, Faith and Community Council",
    testid: "team-tom",
  },
  {
    name: "Gloria Hartwell",
    role: "Heart of Giving | Givers Lane",
    img: IMG.gloria,
    alt: "Gloria Hartwell, Heart of Giving at MyHeartGives",
    testid: "team-gloria",
  },
  {
    name: "Sofia Reyes",
    role: "Champion of Causes | Causes Lane",
    img: IMG.sofia,
    alt: "Sofia Reyes, Champion of Causes at MyHeartGives",
    testid: "team-sofia",
  },
  {
    name: "Phoebe Torres",
    role: "Business & Economic Development Council",
    img: IMG.phoebe,
    alt: "Phoebe Torres, Business and Economic Development Council",
    testid: "team-phoebe",
  },
  {
    name: "Rex Bennett",
    role: "Education & Youth Council",
    img: IMG.rex,
    alt: "Rex Bennett, Education and Youth Council",
    testid: "team-rex",
  },
  {
    name: "Ricco Marino",
    role: "Legacy & Entrepreneurship Council",
    img: "/images/ricco-marino.jpg",
    alt: "Ricco Marino, Legacy and Entrepreneurship Council",
    testid: "team-ricco",
  },
  {
    name: "John Delawder",
    role: "Founder, MyHeartGives.com",
    img: IMG.john,
    alt: "John Delawder, Founder of MyHeartGives",
    testid: "team-john",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const yCollage = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative overflow-hidden"
        data-testid="hero-section"
      >
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 pb-20 pt-14 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:pb-28 lg:pt-24 lg:px-8">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-crimson"
              data-testid="hero-eyebrow"
            >
              A cause-marketing community
            </motion.p>

            <h1
              className="mt-6 font-serif text-4xl font-bold leading-[1.1] tracking-tight text-ink sm:text-5xl lg:text-6xl"
              data-testid="hero-headline"
            >
              <HeroLine delay={0.15}>Communities.</HeroLine>
              <HeroLine delay={0.3}>
                <span className="text-crimson">Causes.</span>
              </HeroLine>
              <HeroLine delay={0.45}>
                <span className="italic">Connection</span>
                <span className="text-gold">.</span>
              </HeroLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="mt-6 max-w-xl text-base leading-relaxed text-cocoa sm:text-lg"
              data-testid="hero-subcopy"
            >
              MyHeartGives is a home where local causes find their voice,
              givers find their purpose, and communities find the support they
              deserve.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/causes"
                className="inline-flex items-center gap-2 rounded-full bg-crimson px-7 py-3.5 text-sm font-semibold text-ivory transition-all duration-200 hover:-translate-y-0.5 hover:bg-crimson-deep"
                data-testid="hero-cta-cause"
              >
                Submit a Cause <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/give"
                className="inline-flex items-center gap-2 rounded-full border-2 border-gold bg-transparent px-7 py-3 text-sm font-semibold text-ink transition-all duration-200 hover:-translate-y-0.5 hover:bg-gold-soft"
                data-testid="hero-cta-give"
              >
                Join the Circle of Givers
              </Link>
            </motion.div>
          </div>

          <motion.div
            style={{ y: yCollage }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
            data-testid="hero-collage"
          >
            <div className="grid grid-cols-2 gap-4 sm:gap-5">
              <div className="relative overflow-hidden rounded-[2rem] border-4 border-gold/60 shadow-2xl">
                <img
                  src={IMG.gloria}
                  alt="Gloria Hartwell, Heart of Giving at MyHeartGives"
                  className="aspect-[3/4] w-full object-cover object-top sepia-[0.12]"
                  loading="eager"
                />
                <span className="absolute bottom-3 left-3 rounded-full bg-ivory/90 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-crimson">
                  Gloria Hartwell
                </span>
              </div>
              <div className="relative overflow-hidden rounded-[2rem] border-4 border-gold/60 shadow-2xl">
                <img
                  src={IMG.sofia}
                  alt="Sofia Reyes, Champion of Causes at MyHeartGives"
                  className="aspect-[3/4] w-full object-cover object-top sepia-[0.12]"
                  loading="eager"
                />
                <span className="absolute bottom-3 left-3 rounded-full bg-ivory/90 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-crimson">
                  Sofia Reyes
                </span>
              </div>
              <div className="col-span-2 overflow-hidden rounded-2xl border-4 border-ivory shadow-lg">
                <img
                  src={IMG.landscape}
                  alt="Misty Appalachian mountains above the New River at golden hour"
                  className="aspect-[21/9] w-full object-cover sepia-[0.15]"
                  loading="lazy"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Marquee
        items={[
          "Listen First",
          "Give Freely",
          "Build Together",
        ]}
      />

      {/* ANTHEM VIDEO */}
      <AnthemVideo
        testidPrefix="anthem"
        eyebrow="Our Story in 95 Seconds"
        headline="This is MyHeartGives"
        subtext="Heart. Story. Community."
        bg="bg-ivory"
      />

      {/* STAY CONNECTED (GotBackup opt-in) */}
      <StayConnected testidPrefix="stay-connected" />

      {/* WHAT IS */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28" data-testid="what-is-section">
        <Reveal>
          <Eyebrow>Three doors, one heart</Eyebrow>
          <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl">
            What is MyHeartGives?
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-3 md:gap-10">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={0.1 * i}>
              <div
                className="group h-full rounded-3xl border border-line bg-white p-8 transition-all duration-200 hover:-translate-y-1.5 hover:border-gold/60 hover:shadow-lg"
                data-testid={p.testid}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-crimson-soft text-crimson transition-colors duration-200 group-hover:bg-crimson group-hover:text-ivory">
                  <p.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-6 font-serif text-xl font-semibold text-ink sm:text-2xl">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-cocoa sm:text-base">
                  {p.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* YOUR COMMUNITY CTA */}
      <section className="bg-cream" data-testid="community-cta-section">
        <div className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6 lg:py-28">
          <Reveal>
            <Eyebrow className="text-center">It begins with one story</Eyebrow>
            <h2
              className="mt-4 font-serif text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl"
              data-testid="community-cta-headline"
            >
              Your community's story starts{" "}
              <span className="italic text-crimson">here.</span>
            </h2>
            <Link
              to="/causes"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-crimson px-8 py-4 text-base font-semibold text-ivory transition-all duration-200 hover:-translate-y-0.5 hover:bg-crimson-deep"
              data-testid="community-cta-button"
            >
              Submit a Cause <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* 5-5-5 INITIATIVE (GotBackup opt-in) */}
      <StayConnected
        testidPrefix="five-five-five"
        campaign="5-5-5- Church outreach"
        eyebrow="5-5-5 Initiative"
        heading={
          <>
            5 Cities. 5 Churches.{" "}
            <span className="italic text-crimson">$5 a Week.</span>
          </>
        }
        subheading="A movement powered by community love."
        buttonLabel="Join the Movement"
      />

      {/* THE PEOPLE BEHIND THE HEART */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28" data-testid="team-section">
        <Reveal>
          <Eyebrow>Meet the MyHeartGives team</Eyebrow>
          <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl">
            The People Behind the Heart
          </h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-cocoa">
            Real people, real lanes, one shared heartbeat — the folks who listen,
            celebrate, and build beside you.
          </p>
        </Reveal>
        <div className="mt-12 grid grid-cols-2 gap-5 sm:gap-6 md:grid-cols-3 lg:grid-cols-4">
          {PEOPLE.map((m, i) => (
            <Reveal key={m.testid} delay={0.06 * i}>
              <div
                className="group h-full overflow-hidden rounded-3xl border border-line bg-white transition-all duration-200 hover:-translate-y-1.5 hover:border-gold/60 hover:shadow-lg"
                data-testid={m.testid}
              >
                <img
                  src={m.img}
                  alt={m.alt}
                  className="aspect-square w-full select-none object-cover object-[50%_20%] sepia-[0.12]"
                  draggable="false"
                  loading="lazy"
                />
                <div className="p-5">
                  <h3 className="font-serif text-base font-semibold text-ink sm:text-lg">
                    {m.name}
                  </h3>
                  <p className="mt-1 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-gold sm:text-[11px]">
                    {m.role}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* EMAIL CAPTURE */}
      <section className="bg-crimson" data-testid="home-capture-section">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <Reveal>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-gold-bright">
              Be part of the launch
            </p>
            <div className="mt-4">
              <EmailCapture
                source="home"
                heading="Join the MyHeartGives Community"
                subheading="One warm letter now and then — launch news, cause stories, and first dibs on founding-member gifts. No noise, ever."
                buttonLabel="Count Me In"
                dark
                testidPrefix="home-email"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
